import type { Home } from '@lmc/ts-common'
import {
  Button,
  Card,
  EmptyState,
  Heading,
  NotStartedIcon,
  PageContentWrapper,
  PageHeader
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Skeleton } from 'antd'
import { ProAdvert } from 'components/generic'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/reports'
import type { FC, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import LineChart from '../components/occupancyReport/LineChart'
import PercentageText from '../components/occupancyReport/PercentageText'
import PieChart from '../components/occupancyReport/PieChart'
import useOccupancyData from '../hooks/useOccupancyData'

interface SectionCardProps {
  title: string
  subTitle: string
  children?: ReactElement
  fullWidth?: boolean
}
const SectionCard = ({
  title,
  subTitle,
  fullWidth,
  children
}: SectionCardProps) => {
  const Container = fullWidth ? FullWidthCard : CardContainer
  return (
    <Container>
      <HeadingStyled level={2}>{title}</HeadingStyled>
      {fullWidth && <br />}
      <ColoredText level={1}>{subTitle}</ColoredText>
      <DividerStyled />
      {children}
    </Container>
  )
}

export const OccupancyScreen: FC = () => {
  const { t } = useTranslation(NAMESPACE.REPORTS)

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const occupancyGranted = home?.features?.bedOccupancy?.granted

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: { preferredPeopleNominative: string } } }) =>
      state.global.home.preferredPeopleNominative
  )?.toLocaleLowerCase()

  const { data, isLoading, error } = useOccupancyData(occupancyGranted)

  const pieData = { ...data?.result?.residentFundingSource }

  const getPieData = () => {
    if (pieData?.Empty) {
      delete Object.assign(pieData, { 'Not set': pieData.Empty }).Empty
    }
    return pieData
  }

  const navigate = useNavigate()

  if (!occupancyGranted) {
    return <ProAdvert />
  }

  if (isLoading) {
    return (
      <PageContentWrapper
        $gap={0}
        $paddingTop={24}
        $paddingX={24}
        $paddingBottom={24}
      >
        <Skeleton />
      </PageContentWrapper>
    )
  }

  if (error) {
    return (
      <PageContentWrapper $gap={0} $paddingTop={24} $paddingBottom={24}>
        Error
      </PageContentWrapper>
    )
  }

  if (!data?.result?.bedCapacity) {
    return (
      <PageContentWrapper $gap={0} $paddingTop={24} $paddingBottom={24}>
        <PageHeader title={t(reportsConstants.title)}>
          <Button
            variant="primary"
            onClick={() => navigate('/settings/home-settings')}
          >
            {t(reportsConstants.btnTitle)}
          </Button>
        </PageHeader>
        <EmptyState
          icon={<NotStartedIcon />}
          title={t(reportsConstants.emptyTitle)}
          description={t(reportsConstants.emptyDesc)}
        />
      </PageContentWrapper>
    )
  }

  return (
    <PageContentWrapper $gap={0} $paddingTop={24} $paddingBottom={24}>
      <PageHeader title={t(reportsConstants.title)} />
      <FlexWraper>
        <SectionCard
          title={t(reportsConstants.founding)}
          subTitle={t(reportsConstants.foundingSub, {
            variable: preferredPeopleNominative
          })}
        >
          <ChartWrapper>
            <PieChart data={getPieData()} />
          </ChartWrapper>
        </SectionCard>
        <SectionCard
          title={t(reportsConstants.currentOccupancy)}
          subTitle={t(reportsConstants.currentOccupancySub, {
            variable: preferredPeopleNominative
          })}
        >
          <PercentageText
            percentage={(
              (100 * data.result.active) /
              data.result.bedCapacity
            ).toFixed(0)}
            label={t(reportsConstants.BEDS_OCCUPIED, {
              occupied: data?.result.active,
              total: data?.result.bedCapacity
            })}
          />
        </SectionCard>
      </FlexWraper>
      <SectionCard
        fullWidth
        // @ts-ignore
        title={t(reportsConstants.overtime)}
        subTitle={t(reportsConstants.overtimeSub, {
          variable: preferredPeopleNominative
        })}
      >
        <ChartWrapper>
          <LineChart data={data?.result?.activeResidentSeries} />
        </ChartWrapper>
      </SectionCard>
    </PageContentWrapper>
  )
}

const DividerStyled = styled(Divider)`
  margin-top: 16px;
`

const ColoredText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

const FullWidthCard = styled(Card)`
  width: 100%;
`

const CardContainer = styled(Card)`
  display: flex;
  flex-direction: column;
`
const FlexWraper = styled.div`
  display: flex;
  gap: 24px;
  max-height: 380px;
  margin-bottom: 24px;
  margin-top: 32px;

  > div {
    flex: 1;
  }
`

const HeadingStyled = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3} !important;
`

const ChartWrapper = styled.div`
  margin-top: 20px;
`
